/**
 * Identity bar of the map's site detail panel: which site is open, how it is
 * doing, and the three ways out of it — back to the list, back to the marker on
 * the map, or on to the site's full dashboard in a new tab.
 *
 * Health reads as the site dashboard's own status dot beside the name, not as a
 * pill of the map's, so opening a site from the map and opening its dashboard
 * say the same thing in the same place.
 *
 * Contains:
 * - SiteDetailHeader — the panel's header row (default export)
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import DashboardStatusDot, { dashboardStatusColor } from '@sophocles/components/BasicDashboardCard/DashboardStatusDot';
import StatusChip from '@sophocles/components/StatusChip';
import { SITE_STATUS } from '@sophocles/features/map/mapState';
import { siteStatusLabel } from '@sophocles/views/map/mapStatusLabels';

/**
 * @param {object} props
 * @param {string} props.siteName - the site's name.
 * @param {?string} props.siteGroupName - the group the site belongs to.
 * @param {?string} props.status - a deriveSiteCardStatus token for the dot's colour.
 * @param {string} props.statusWording - the SITE_STATUS the dot is named by,
 *   from deriveSiteStatusWording.
 * @param {boolean} props.deactivated - the site is deactivated.
 * @param {Function} props.onBack - leave the panel for the site list.
 * @param {Function} props.onFindOnMap - centre the map on this site.
 * @param {string} props.dashboardHref - path to the site's full dashboard.
 */
export default function SiteDetailHeader({
  siteName,
  siteGroupName,
  status,
  statusWording,
  deactivated,
  onBack,
  onFindOnMap,
  dashboardHref,
}) {
  const { t } = useTranslation();

  const backLabel = t('actions.back', 'Back');
  const findLabel = t('map.findOnMap', 'Find on map');
  const dashboardLabel = t('general.dashboard', 'Dashboard');

  return (
    <Box
      sx={{
        flex: 'none',
        px: 'var(--space-2)',
        py: 'var(--space-2)',
        borderBottom: '1px solid var(--border-default)',
        bgcolor: 'var(--surface-primary)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
        <Tooltip title={backLabel}>
          <IconButton aria-label={backLabel} onClick={onBack} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <DashboardStatusDot
          color={dashboardStatusColor(status, 'var(--status-neutral)')}
          title={siteStatusLabel(t, statusWording)}
        />

        <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} title={siteName}>
          {siteName}
        </Typography>

        <Tooltip title={dashboardLabel}>
          <IconButton
            aria-label={dashboardLabel}
            href={dashboardHref}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={findLabel}>
          <IconButton aria-label={findLabel} onClick={onFindOnMap} size="small">
            <MyLocationIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {deactivated || siteGroupName ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 'var(--space-1)', pl: 'var(--space-1)', minWidth: 0, flexWrap: 'wrap', rowGap: 'var(--space-1)' }}
        >
          {deactivated ? <StatusChip color="neutral" label={t('general.deactivated', 'Deactivated')} /> : null}
          {siteGroupName ? (
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', minWidth: 0 }} title={siteGroupName}>
              {siteGroupName}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

SiteDetailHeader.propTypes = {
  siteName: PropTypes.string,
  siteGroupName: PropTypes.string,
  status: PropTypes.string,
  statusWording: PropTypes.oneOf(Object.values(SITE_STATUS)).isRequired,
  deactivated: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onFindOnMap: PropTypes.func.isRequired,
  dashboardHref: PropTypes.string.isRequired,
};
